import React, {FC, useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {
  createAttachmentPreviewUrl,
  downloadAttachmentBlob,
  previewAttachmentBlob,
} from '../core/attachmentUtils'

type AttachmentLike = {
  id?: number | string | null
  fileName: string
  url?: string
  attachmentKey?: string | null
  sizeBytes?: number | null
  available?: boolean | null
}

interface ProcurementAttachmentGalleryProps {
  attachments?: AttachmentLike[] | null
  emptyText?: string
  showViewButton?: boolean
}

const isImageFile = (fileName?: string | null) =>
  /\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(String(fileName || ''))

const formatFileSize = (size?: number | null) => {
  if (size == null || Number.isNaN(size)) return '-'
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

const ProcurementAttachmentGallery: FC<ProcurementAttachmentGalleryProps> = ({
  attachments,
  emptyText = 'No attachments available.',
  showViewButton = true,
}) => {
  const listedAttachments = useMemo(
    () =>
      (attachments || []).filter(
        (attachment) => attachment && String(attachment.fileName || '').trim()
      ),
    [attachments]
  )
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null)
  const [imagePreviewName, setImagePreviewName] = useState<string>('')
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({})

  useEffect(() => {
    let cancelled = false
    const createdUrls: string[] = []

    const loadThumbnails = async () => {
      const next: Record<string, string> = {}
      for (const attachment of listedAttachments) {
        if (attachment.available === false || !isImageFile(attachment.fileName)) continue
        try {
          const blobUrl = await createAttachmentPreviewUrl(attachment)
          if (cancelled) {
            window.URL.revokeObjectURL(blobUrl)
            continue
          }
          createdUrls.push(blobUrl)
          next[`${attachment.id ?? attachment.fileName}-${attachment.fileName}`] = blobUrl
        } catch {
          // Thumbnail load failure is non-fatal; download action still available.
        }
      }
      if (!cancelled) {
        setThumbnailUrls(next)
      }
    }

    void loadThumbnails()

    return () => {
      cancelled = true
      createdUrls.forEach((url) => window.URL.revokeObjectURL(url))
    }
  }, [listedAttachments])

  if (!listedAttachments.length) {
    return <div className='text-muted'>{emptyText}</div>
  }

  return (
    <>
      <div className='d-flex flex-wrap gap-3'>
        {listedAttachments.map((attachment, index) => {
          const unavailable = attachment.available === false
          const thumbKey = `${attachment.id ?? attachment.fileName}-${attachment.fileName}`
          const image = isImageFile(attachment.fileName)
          const thumbnail = thumbnailUrls[thumbKey]
          return (
            <div
              key={`${attachment.fileName}-${index}`}
              className='border rounded p-2 bg-light'
              style={{minWidth: '220px', maxWidth: '280px'}}
            >
              <div className='d-flex gap-2 align-items-start'>
                <div
                  className='border rounded bg-white d-flex justify-content-center align-items-center flex-shrink-0'
                  style={{width: '52px', height: '52px', overflow: 'hidden'}}
                >
                  {unavailable ? (
                    <span className='text-muted fs-8 px-1 text-center'>N/A</span>
                  ) : image && thumbnail ? (
                    <img
                      src={thumbnail}
                      alt={attachment.fileName}
                      style={{width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer'}}
                      onClick={async () => {
                        try {
                          setBusyKey(`${index}-preview`)
                          const blobUrl = await createAttachmentPreviewUrl(attachment)
                          setImagePreviewUrl(blobUrl)
                          setImagePreviewName(attachment.fileName)
                        } catch (error: any) {
                          toast.error('Failed to preview attachment')
                        } finally {
                          setBusyKey(null)
                        }
                      }}
                    />
                  ) : (
                    <span className='fw-bold text-muted fs-8'>
                      {attachment.fileName.split('.').pop()?.toUpperCase() || 'FILE'}
                    </span>
                  )}
                </div>
                <div className='flex-grow-1' style={{minWidth: 0}}>
                  <div className='fw-semibold text-dark text-truncate mb-1' title={attachment.fileName}>
                    {attachment.fileName}
                  </div>
                  <div className='text-muted fs-8 mb-2'>{formatFileSize(attachment.sizeBytes)}</div>
                  {unavailable ? (
                    <span className='badge bg-light-warning text-warning'>Attachment unavailable</span>
                  ) : (
                    <div className='d-flex gap-2 flex-wrap'>
                      {showViewButton && image && (
                        <button
                          type='button'
                          className='btn btn-sm btn-light py-1 px-2'
                          disabled={busyKey === `${index}-preview`}
                          onClick={async () => {
                            try {
                              setBusyKey(`${index}-preview`)
                              await previewAttachmentBlob(attachment)
                            } catch (error: any) {
                              toast.error('Failed to preview attachment')
                            } finally {
                              setBusyKey(null)
                            }
                          }}
                        >
                          View
                        </button>
                      )}
                      <button
                        type='button'
                        className='btn btn-sm btn-light-primary py-1 px-2'
                        disabled={busyKey === `${index}-download`}
                        onClick={async () => {
                          try {
                            setBusyKey(`${index}-download`)
                            await downloadAttachmentBlob(attachment, attachment.fileName)
                          } catch (error: any) {
                            toast.error('Failed to download attachment')
                          } finally {
                            setBusyKey(null)
                          }
                        }}
                      >
                        {busyKey === `${index}-download` ? 'Downloading…' : 'Download'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {imagePreviewUrl && (
        <div
          className='modal fade show d-flex align-items-center justify-content-center'
          tabIndex={-1}
          style={{
            backgroundColor: 'rgba(0,0,0,0.7)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 1080,
          }}
        >
          <div className='modal-dialog modal-xl modal-dialog-centered'>
            <div className='modal-content bg-white'>
              <div className='modal-header'>
                <h5 className='modal-title text-truncate' style={{maxWidth: '90%'}}>
                  {imagePreviewName}
                </h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => {
                    window.URL.revokeObjectURL(imagePreviewUrl)
                    setImagePreviewUrl(null)
                  }}
                />
              </div>
              <div className='modal-body text-center bg-light'>
                <img
                  src={imagePreviewUrl}
                  alt={imagePreviewName}
                  style={{maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain'}}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ProcurementAttachmentGallery
